
import { computed, defineComponent, onBeforeMount, PropType, reactive } from 'vue'

import NewModal from '@/common/new-components/NewModal/index.vue'

import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'

import { Comment, TakeOrder, WaitListStatus } from '@/new-services/types/takes'

import takesAPI from '@/new-services/takes.service'

import useNotifications from '@/common/composable/useNotifications'

import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'
import { OktellCreateWaitListParams } from '@/core/types/oktell.types'
import { oktell } from '@/core/realisations/oktell.realisation'

const STATUSES_CODES_WITH_DATE = [319, 334, 336, 337, 338, 301, 307, 310, 304, 316, 345]

export default defineComponent({
    components: {
        NewModal,
        NewSelect,
        NewInput,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        order: {
            type: Object as PropType<TakeOrder>,
            required: true,
        },
        type: {
            type: Number,
            default: 3,
        },
        isInventory: {
            type: Boolean,
            default: false,
        },
        code: {
            type: Number,
            default: null,
        },
    },
    emits: ['submit', 'cancel'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_other: false,
            statusCode: null as number | null,
            value: null as string | null,
            comment: null as string | null,
            comment_other: null as string | null,
        })

        const isDate = computed(() =>
            STATUSES_CODES_WITH_DATE.includes(state.statusCode as number)
        )

        const validation$ = useVuelidate(
            {
                statusCode: { required },
                value: { required: requiredIf(() => isDate.value) },
            },
            state
        )

        const statuses_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as WaitListStatus[],
        })

        const comments_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as Comment[],
        })

        const loadStatuses = async () => {
            try {
                statuses_state.is_loading = true

                const response = await takesAPI.getWaitListStatuses({
                    type: props.type,
                    isDispatcher: 1,
                })
                statuses_state.list = response.data.data
                if (props.isInventory) {
                    statuses_state.list = response.data.data.filter((status) => status.code === 234)
                }
                // if (props.code) {
                //     statuses_state.list = response.data.data.filter(
                //         (status) => status.code === props.code
                //     )
                // }

                statuses_state.is_failed = false
            } catch (error) {
                console.error(error)
                statuses_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка статусов произошла ошибка',
                })
            } finally {
                statuses_state.is_loading = false
            }
        }

        const loadComments = async () => {
            try {
                comments_state.is_loading = true

                const typeId = props.type === 2 ? 2 : 1
                const response = await takesAPI.getComments(typeId)
                comments_state.list = response.data.data
                comments_state.list.push({ text: 'Другое' })

                comments_state.is_failed = false
            } catch (error) {
                console.error(error)
                comments_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка произошла ошибка',
                })
            } finally {
                comments_state.is_loading = false
            }
        }

        const onSubmit = async () => {
            if (props.loading) return

            validation$.value.$touch()
            if (validation$.value.$invalid) return

            try {
                state.is_loading = true

                const comment = state.is_other ? state.comment_other : state.comment

                const payload: OktellCreateWaitListParams = {
                    code: state.statusCode as number,
                    stateId: 48,
                    value: state.value as string,
                }

                if (props.type === 3) {
                    payload.orderId = props.order.orderId
                } else if (props.type === 2) {
                    payload.invoiceId = props.order.invoiceId
                }
                if (comment) payload.comment = comment

                await oktell.createWaitList(payload)
                notifications.notify({ title: 'Ответ успешно сохранен!', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При попытке оставить ответ произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }

            emit('submit')
        }

        const onCancel = () => {
            if (props.loading) return
            emit('cancel')
        }

        const onStatusChange = () => {
            if (!isDate.value) state.value = null
        }

        const onCommentChange = (value: string) => {
            state.comment_other = null

            if (value === 'Другое') state.is_other = true
            else state.is_other = false
        }

        const isLoading = computed(() => {
            return (
                props.loading ||
                state.is_loading ||
                statuses_state.is_loading ||
                comments_state.is_loading
            )
        })

        onBeforeMount(() => {
            loadStatuses()
            loadComments()
        })

        return {
            STATUSES_CODES_WITH_DATE,
            state,
            validation$,
            statuses_state,
            comments_state,
            loadStatuses,
            loadComments,
            onSubmit,
            onCancel,
            onStatusChange,
            onCommentChange,
            //
            isDate,
            isLoading,
        }
    },
})
